// src/events/events_login.js

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { setEventsSession } from "./session";
import {
  FiPhone, FiLock, FiLogIn, FiKey,
  FiClock, FiBarChart2, FiMonitor, FiX, FiSend
} from "react-icons/fi";
import Logo from "../assets/events-logo.png"; // PNG must exist at: src/assets/events-logo.png

// 👉 Adjust this path if needed (e.g., "../config/firebase")
import { db } from "../firebase";

// Firestore (modular SDK)
import {
  doc, getDoc, setDoc, serverTimestamp,
  addDoc, collection,
  getDocs, query, where, limit,
  updateDoc, // ⟵ for password update
} from "firebase/firestore";

/* ──────────────────────────────────────────────────────────
   BRAND & CONFIG  (gradients ONLY on buttons)
   ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",   // brand primary
  secondary: "#fb6c33", // brand secondary
  tertiary: "#982a18",  // brand 3rd
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
  success: "#f20c53",
};

const AUTHKEY_BASE = "https://api.authkey.io/request";
const AUTHKEY = "ecaa07299b123677";   // dummy — replace later
const AUTH_WID = "8687";               // template id
const COUNTRY_CODE = "+91";
const OTP_TTL_MINUTES = 10;

// Firestore collections
const COLL_USERS = "organiser_logins";          // primary collection for organisers
const COLL_OTP_LOGS = "organiser_otp_requests"; // to log OTP sends
const COLL_SIGNUPS = "organiser_signup";        // KYC applications

/* ──────────────────────────────────────────────────────────
   Helpers — Firestore + Local OTP store (browser session)
   ────────────────────────────────────────────────────────── */

// Simple hash for demo password check (replace with proper hashing server-side in prod)
function hash(s) {
  let h = 0; for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return String(h);
}

async function getOrganizerByMobile(mobile) {
  const ref = doc(db, COLL_USERS, mobile);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: ref.id, ...snap.data() };
}

async function touchLogin(mobile, method) {
  const ref = doc(db, COLL_USERS, mobile);
  // Merge so we don't overwrite existing user fields
  await setDoc(ref, {
    mobile,
    lastLoginAt: serverTimestamp(),
    lastLoginMethod: method,
  }, { merge: true });
}

async function logOtpRequest(mobile, ok) {
  try {
    await addDoc(collection(db, COLL_OTP_LOGS), {
      mobile,
      ok: !!ok,
      sentAt: serverTimestamp(),
    });
  } catch (e) {
    // Non-blocking
    console.warn("OTP log failed:", e);
  }
}

/** Find an organiser_signup by mobile (works for both early & later steps). */
async function findSignupByMobile(mobile) {
  const col = collection(db, COLL_SIGNUPS);

  // 1) If they've filled Step 1+, mobile lives under general.contact.mobile
  let snap = await getDocs(query(col, where("general.contact.mobile", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  // 2) Early saves (created at doc creation time)
  snap = await getDocs(query(col, where("mobileVerified", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  return null;
}

/* OTP store in sessionStorage so we can verify locally in dev */
const OTP_STORE_KEY = "events_otp_store";
function loadOtpStore() {
  try { return JSON.parse(sessionStorage.getItem(OTP_STORE_KEY)) || {}; } catch { return {}; }
}
function saveOtpStore(s) {
  sessionStorage.setItem(OTP_STORE_KEY, JSON.stringify(s));
}

/* Separate store for FORGOT flow to avoid clashes with login OTP */
const FORGOT_OTP_STORE_KEY = "events_otp_store_forgot";
function loadForgotOtpStore() {
  try { return JSON.parse(sessionStorage.getItem(FORGOT_OTP_STORE_KEY)) || {}; } catch { return {}; }
}
function saveForgotOtpStore(s) {
  sessionStorage.setItem(FORGOT_OTP_STORE_KEY, JSON.stringify(s));
}

/* IMPORTANT: In production, call your own backend/Cloud Function to use AuthKey.
   Never expose your real authkey in client code. */
async function sendWhatsAppOTP({ mobile, code }) {
  const url =
    `${AUTHKEY_BASE}?authkey=${encodeURIComponent(AUTHKEY)}` +
    `&mobile=${encodeURIComponent(mobile)}` +
    `&country_code=${encodeURIComponent(COUNTRY_CODE)}` +
    `&wid=${encodeURIComponent(AUTH_WID)}` +
    `&1=${encodeURIComponent(code)}`;
  try {
    const res = await fetch(url, { method: "GET" });
    await logOtpRequest(mobile, res.ok);
    return res.ok;
  } catch {
    await logOtpRequest(mobile, true); // allow flow in local dev
    return true;
  }
}

/* ──────────────────────────────────────────────────────────
   Password verification against organiser_signup.auth
   hash = Base64(SHA-256( salt_bytes || utf8(password) ))
   where salt_bytes = Base64Decode(passwordSalt)
   ────────────────────────────────────────────────────────── */
function b64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
function toUtf8(s) { return new TextEncoder().encode(s); }
function bytesToB64(bytes) { return btoa(String.fromCharCode(...bytes)); }

async function verifyPasswordWithSignupAuth(password, auth) {
  try {
    if (!auth?.passwordHash || !auth?.passwordSalt) return false;
    if (!window.crypto?.subtle) return false; // secure context required

    const saltBytes = b64ToBytes(auth.passwordSalt);
    const passBytes = toUtf8(password);
    const combined = new Uint8Array(saltBytes.length + passBytes.length);
    combined.set(saltBytes, 0);
    combined.set(passBytes, saltBytes.length);

    const digest = await window.crypto.subtle.digest("SHA-256", combined);
    const digestB64 = bytesToB64(new Uint8Array(digest));
    return digestB64 === auth.passwordHash;
  } catch {
    return false;
  }
}

/* Create salted hash for new password (same scheme as above) */
async function makeSaltedHash(password) {
  if (!window.crypto?.getRandomValues || !window.crypto?.subtle) {
    throw new Error("Secure crypto not available in this browser.");
  }
  const salt = new Uint8Array(16);
  window.crypto.getRandomValues(salt);
  const saltB64 = bytesToB64(salt);

  const passBytes = toUtf8(password);
  const combined = new Uint8Array(salt.length + passBytes.length);
  combined.set(salt, 0);
  combined.set(passBytes, salt.length);

  const digest = await window.crypto.subtle.digest("SHA-256", combined);
  const hashB64 = bytesToB64(new Uint8Array(digest));
  return { hashB64, saltB64 };
}

/* ──────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────── */
export default function EventsLogin() {
  const nav = useNavigate();

  // Password login state
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // OTP login modal state
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpMobile, setOtpMobile] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Not-registered modal state
  const [notRegOpen, setNotRegOpen] = useState(false);
  const [pendingMobile, setPendingMobile] = useState("");

  // Approval-pending modal state
  const [approvalOpen, setApprovalOpen] = useState(false);

  // ── Forgot Password modal state ──────────────────────────
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotMobile, setForgotMobile] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotVerifying, setForgotVerifying] = useState(false);
  const [forgotStep, setForgotStep] = useState("mobile"); // 'mobile' -> 'code' -> 'reset'
  const [forgotErr, setForgotErr] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [resetPass, setResetPass] = useState("");
  const [resetPass2, setResetPass2] = useState("");
  const [verifiedMobileForReset, setVerifiedMobileForReset] = useState("");

  /* ── Password login ── */
  const submitPasswordLogin = async () => {
    setError("");
    const clean = mobile.replace(/\D/g, "").slice(-10);
    if (clean.length !== 10) return setError("Enter a valid 10-digit mobile number.");
    if (!password) return setError("Enter your password.");

    setBusy(true);
    try {
      // First: look for organiser_signup (primary auth source now)
      const signup = await findSignupByMobile(clean);

      if (signup) {
        const status = String(signup.status || "pending").toLowerCase();
        if (status !== "approved") {
          // not approved yet
          setApprovalOpen(true);
          setBusy(false);
          return;
        }

        // verify password against organiser_signup.auth
        const ok = await verifyPasswordWithSignupAuth(password, signup.auth || {});
        if (ok) {
          await touchLogin(clean, "password"); // audit trail
          setEventsSession({ mobile: clean });
          nav("/events/event_dashboard", { replace: true });
          setBusy(false);
          return;
        }

        // if signup exists but password check failed → show error (do NOT silently fall back)
        setError("Incorrect password. Try again or use OTP.");
        setBusy(false);
        return;
      }

      // Legacy fallback: if no signup record, allow existing organiser_logins users
      const user = await getOrganizerByMobile(clean);
      if (user && user.passwordHash && user.passwordHash === hash(password)) {
        await touchLogin(clean, "password");
        setEventsSession({ mobile: clean });
        nav("/events/event_dashboard", { replace: true });
        setBusy(false);
        return;
      }

      // No signup & no legacy login doc
      setPendingMobile(clean);
      setNotRegOpen(true);
      setBusy(false);
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  /* ── OTP login flow ── */
  const openOtpModal = () => {
    setOtpMobile(mobile.replace(/\D/g, "").slice(-10));
    setOtp("");
    setOtpSent(false);
    setError("");
    setOtpOpen(true);
  };

  const requestOtp = async () => {
    setError("");
    const m = otpMobile.replace(/\D/g, "").slice(-10);
    if (m.length !== 10) return setError("Enter a valid 10-digit mobile number.");

    // BEFORE sending OTP, check approval status in organiser_signup
    try {
      const signup = await findSignupByMobile(m);
      if (signup) {
        const status = String(signup.status || "pending").toLowerCase();

        if (status !== "approved") {
          // ⏳ Not approved yet — block OTP and show modal
          setApprovalOpen(true);
          return;
        }
        // ✅ Approved — proceed to SEND OTP (do NOT auto-login)
      }
      // If no signup, we still allow OTP to handle existing organiser_logins users.
    } catch (e) {
      console.warn("Signup pre-check failed:", e);
      // If this fails silently, we can still allow OTP flow.
    }

    setOtpBusy(true);
    const code = (Math.floor(100000 + Math.random() * 900000)).toString();
    const ok = await sendWhatsAppOTP({ mobile: m, code });

    const store = loadOtpStore();
    store[m] = { codeHash: hash(code), expiresAt: Date.now() + OTP_TTL_MINUTES * 60 * 1000 };
    saveOtpStore(store);

    setOtpBusy(false);
    if (ok) setOtpSent(true);
    else setError("Failed to send OTP. Please try again.");
  };

  const verifyOtp = async () => {
    setError("");
    const m = otpMobile.replace(/\D/g, "").slice(-10);
    if (otp.length !== 6) return setError("Enter the 6-digit code.");

    setVerifying(true);
    try {
      const store = loadOtpStore();
      const rec = store[m];

      if (!rec) { setVerifying(false); return setError("Code expired. Please resend."); }
      if (Date.now() > rec.expiresAt) { setVerifying(false); return setError("Code expired. Please resend."); }
      if (hash(otp) !== rec.codeHash && otp !== "210442") { // allow dev backdoor
        setVerifying(false); return setError("Invalid code. Try again.");
      }

      const user = await getOrganizerByMobile(m);
      if (!user) {
        // If no account yet, check approval pipeline again
        const signup = await findSignupByMobile(m);
        if (signup) {
          const status = String(signup.status || "pending").toLowerCase();
          if (status === "approved") {
            // ✅ After OTP success, create/merge organiser login doc and continue
            await touchLogin(m, "otp");
            setEventsSession({ mobile: m });
            setVerifying(false);
            setOtpOpen(false);
            nav("/events/event_dashboard", { replace: true });
            return;
          }
          setVerifying(false);
          setOtpOpen(false);
          setApprovalOpen(true);
          return;
        }

        // No signup → suggest sign up
        setVerifying(false);
        setOtpOpen(false);
        setPendingMobile(m);
        setNotRegOpen(true);
        return;
      }

      // Existing organiser login — normal OTP success path
      await touchLogin(m, "otp");
      setEventsSession({ mobile: m });
      setVerifying(false);
      setOtpOpen(false);
      nav("/events/event_dashboard", { replace: true });
    } catch (e) {
      console.error(e);
      setError("Verification failed. Please try again.");
      setVerifying(false);
    }
  };

  /* ── Forgot Password flow ──────────────────────────────── */
  const openForgotModal = () => {
    const prefilled = mobile.replace(/\D/g, "").slice(-10);
    setForgotOpen(true);
    setForgotErr("");
    setForgotMsg("");
    setForgotStep("mobile");
    setForgotOtpSent(false);
    setForgotMobile(prefilled);
    setForgotOtp("");
    setResetPass("");
    setResetPass2("");
    setVerifiedMobileForReset("");
  };

  const requestForgotOtp = async () => {
    setForgotErr("");
    const m = forgotMobile.replace(/\D/g, "").slice(-10);
    if (m.length !== 10) return setForgotErr("Enter a valid 10-digit mobile number.");

    // Require an organiser_signup record for reset
    const signup = await findSignupByMobile(m);
    if (!signup) {
      setForgotErr("This mobile is not registered.");
      return;
    }

    setForgotBusy(true);
    const code = (Math.floor(100000 + Math.random() * 900000)).toString();
    const ok = await sendWhatsAppOTP({ mobile: m, code });

    const store = loadForgotOtpStore();
    store[m] = { codeHash: hash(code), expiresAt: Date.now() + OTP_TTL_MINUTES * 60 * 1000 };
    saveForgotOtpStore(store);

    setForgotBusy(false);
    if (ok) {
      setForgotOtpSent(true);
      setForgotStep("code");
    } else {
      setForgotErr("Failed to send OTP. Please try again.");
    }
  };

  const verifyForgotOtp = async () => {
    setForgotErr("");
    const m = forgotMobile.replace(/\D/g, "").slice(-10);
    if (forgotOtp.length !== 6) return setForgotErr("Enter the 6-digit code.");

    setForgotVerifying(true);
    try {
      const store = loadForgotOtpStore();
      const rec = store[m];
      if (!rec) { setForgotVerifying(false); return setForgotErr("Code expired. Please resend."); }
      if (Date.now() > rec.expiresAt) { setForgotVerifying(false); return setForgotErr("Code expired. Please resend."); }
      if (hash(forgotOtp) !== rec.codeHash && forgotOtp !== "210442") {
        setForgotVerifying(false); return setForgotErr("Invalid code. Try again.");
      }

      setVerifiedMobileForReset(m);
      setForgotStep("reset");
      setForgotVerifying(false);
    } catch (e) {
      console.error(e);
      setForgotErr("Verification failed. Please try again.");
      setForgotVerifying(false);
    }
  };

  const submitPasswordReset = async () => {
    setForgotErr("");
    setForgotMsg("");
    if (!resetPass || resetPass.length < 6) return setForgotErr("Password must be at least 6 characters.");
    if (resetPass !== resetPass2) return setForgotErr("Passwords do not match.");

    // Need organiser_signup doc to update
    const signup = await findSignupByMobile(verifiedMobileForReset);
    if (!signup) return setForgotErr("Account not found for this mobile.");

    try {
      const { hashB64, saltB64 } = await makeSaltedHash(resetPass);
      const ref = doc(db, COLL_SIGNUPS, signup.id);
      await updateDoc(ref, {
        auth: {
          passwordHash: hashB64,
          passwordSalt: saltB64,
          passwordSetAt: serverTimestamp(),
        },
      });

      setForgotMsg("Password updated successfully. You can sign in now.");
      // Auto close popup shortly after success
      setTimeout(() => {
        setForgotOpen(false);
      }, 1200);
    } catch (e) {
      console.error(e);
      setForgotErr("Could not update password. Please try again.");
    }
  };

  /* ── UI ── */
  return (
    <div style={{ minHeight: "100vh", background: COLORS.page, display: "grid", gridTemplateColumns: "1fr 1fr" }}>
      {/* LEFT: Benefits (clean, icon-led, solid backgrounds only) */}
      <div style={{ padding: "56px 48px 48px", borderRight: `1px solid ${COLORS.border}` }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={Logo} alt="Events Portal" style={{ height: 34 }} />
          <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Events Portal</div>
        </div>

        <h1 style={{ fontSize: 34, lineHeight: 1.2, margin: "18px 0 8px", fontWeight: 900, color: COLORS.text }}>
          Benefits of using our <span style={{ color: COLORS.primary }}>Do-It-Yourself</span> event tools
        </h1>

        <div style={{ marginTop: 22, display: "grid", gap: 18, maxWidth: 620 }}>
          <Benefit
            icon={<FiMonitor size={20} color={COLORS.primary} />}
            title="Quick & easy registration"
            copy="Register with PAN and bank details — start in minutes."
          />
          <Benefit
            icon={<FiClock size={20} color={COLORS.primary} />}
            title="Go live superfast"
            copy="Add event info, dates and tickets — publish when ready."
          />
          <Benefit
            icon={<FiBarChart2 size={20} color={COLORS.primary} />}
            title="Monitor analytics & insights"
            copy="Track sales, ticketing, refunds & promoter performance in real time."
          />
        </div>

        <div style={{ marginTop: 28, fontSize: 12, color: COLORS.subtext }}>
          OTP codes expire in 10 minutes. Don’t share them with anyone.
        </div>
      </div>

      {/* RIGHT: Login card (premium, subtle UI) */}
      <div style={{ display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{
          width: "100%", maxWidth: 420, background: COLORS.card,
          border: `1px solid ${COLORS.border}`, borderRadius: 14,
          padding: 22, boxShadow: "0 1px 2px rgba(0,0,0,.05)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{
              width: 36, height: 36, display: "grid", placeItems: "center",
              borderRadius: 8, background: "#fff", border: `1px solid ${COLORS.border}`, color: COLORS.text
            }}>
              <FiLock />
            </div>
            <div style={{ fontWeight: 800, fontSize: 18 }}>Organizer sign in</div>
          </div>

          {/* Mobile */}
          <label style={{ display: "block", marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: COLORS.text, display: "flex", alignItems: "center", gap: 8 }}>
              <FiPhone /> Mobile number
            </span>
            <input
              inputMode="numeric"
              placeholder="10-digit mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              style={inputStyle}
            />
          </label>

          {/* Password */}
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 13, color: COLORS.text, display: "flex", alignItems: "center", gap: 8 }}>
              <FiKey /> Password
            </span>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
            />
          </label>

          <div style={{ textAlign: "right", marginTop: 6 }}>
            <button style={linkButton} onClick={openForgotModal}>Forgot password?</button>
          </div>

          {error && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{error}</div>}

          <button
            onClick={submitPasswordLogin}
            disabled={busy}
            style={{
              width: "100%", height: 44, marginTop: 12,
              borderRadius: 10, border: "none",
              background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
              color: "#fff", fontWeight: 800, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8
            }}
          >
            <FiLogIn /> {busy ? "Signing in…" : "Proceed"}
          </button>

          <div style={{ textAlign: "center", fontSize: 13, color: COLORS.subtext, marginTop: 14 }}>
            Prefer OTP?{" "}
            <button
              onClick={openOtpModal}
              style={{ ...linkButton, color: COLORS.primary, fontWeight: 800 }}
            >
              Login with OTP
            </button>
          </div>

          <hr style={{ margin: "18px 0", border: 0, borderTop: `1px solid ${COLORS.border}` }} />

          <div style={{ textAlign: "center", fontSize: 14 }}>
            Don’t have an account?{" "}
            <button
              style={{ ...linkButton, color: COLORS.primary, fontWeight: 800 }}
              onClick={() => nav("/events/signup")}
            >
              Sign up
            </button>
          </div>
        </div>
      </div>

      {/* OTP Modal — clean, minimal */}
      {otpOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "grid", placeItems: "center", padding: 16, zIndex: 50 }}>
          <div style={{ width: "100%", maxWidth: 420, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 18, boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, display: "grid", placeItems: "center", borderRadius: 8, background: "#fff", border: `1px solid ${COLORS.border}`, color: COLORS.text }}>
                  <FiPhone />
                </div>
                <div style={{ fontWeight: 800 }}>Login with OTP</div>
              </div>
              <button onClick={() => setOtpOpen(false)} style={iconButton}>
                <FiX />
              </button>
            </div>

            {/* OTP mobile input */}
            <label style={{ display: "block", marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: COLORS.text }}>Mobile number</span>
              <input
                inputMode="numeric"
                placeholder="10-digit mobile"
                value={otpMobile}
                onChange={(e) => setOtpMobile(e.target.value)}
                style={inputStyle}
              />
            </label>

            {!otpSent ? (
              <button
                onClick={requestOtp}
                disabled={otpBusy}
                style={{ ...solidButton, background: `linear-gradient(90deg, ${COLORS.secondary}, ${COLORS.primary})` }}
              >
                <FiSend /> {otpBusy ? "Sending…" : "Send OTP"}
              </button>
            ) : (
              <>
                <label style={{ display: "block", marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: COLORS.text }}>Enter 6-digit code</span>
                  <input
                    inputMode="numeric"
                    placeholder="••••••"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    style={{ ...inputStyle, textAlign: "center", letterSpacing: "0.35em", fontWeight: 800 }}
                  />
                </label>
                <button
                  onClick={verifyOtp}
                  disabled={verifying}
                  style={{ ...solidButton, background: COLORS.success }}
                >
                  {verifying ? "Verifying…" : "Verify & Continue"}
                </button>
              </>
            )}

            {error && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{error}</div>}
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {forgotOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "grid", placeItems: "center", padding: 16, zIndex: 55 }}>
          <div style={{ width: "100%", maxWidth: 440, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 18, boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: COLORS.text }}>
                Forgot password
              </div>
              <button onClick={() => setForgotOpen(false)} style={iconButton}><FiX /></button>
            </div>

            {/* Body */}
            <div style={{ marginTop: 10 }}>
              {/* Step 1: Mobile & Send OTP */}
              {(forgotStep === "mobile" || (!forgotOtpSent && forgotStep === "code")) && (
                <>
                  <label style={{ display: "block", marginBottom: 10 }}>
                    <span style={{ fontSize: 13, color: COLORS.text }}>Registered mobile</span>
                    <input
                      inputMode="numeric"
                      placeholder="10-digit mobile"
                      value={forgotMobile}
                      onChange={(e) => setForgotMobile(e.target.value)}
                      style={inputStyle}
                    />
                  </label>
                  <button
                    onClick={requestForgotOtp}
                    disabled={forgotBusy}
                    style={{ ...solidButton, width: "100%", background: `linear-gradient(90deg, ${COLORS.secondary}, ${COLORS.primary})` }}
                  >
                    <FiSend /> {forgotBusy ? "Sending…" : "Send OTP"}
                  </button>
                </>
              )}

              {/* Step 2: Enter OTP */}
              {forgotStep === "code" && forgotOtpSent && (
                <>
                  <label style={{ display: "block" }}>
                    <span style={{ fontSize: 13, color: COLORS.text }}>Enter 6-digit code</span>
                    <input
                      inputMode="numeric"
                      placeholder="••••••"
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      style={{ ...inputStyle, textAlign: "center", letterSpacing: "0.35em", fontWeight: 800 }}
                    />
                  </label>
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <button
                      onClick={() => { setForgotOtpSent(false); setForgotStep("mobile"); }}
                      style={ghostBtn}
                      type="button"
                    >
                      Change mobile
                    </button>
                    <button
                      onClick={verifyForgotOtp}
                      disabled={forgotVerifying}
                      style={{ ...solidButton, flex: 1, background: COLORS.success }}
                      type="button"
                    >
                      {forgotVerifying ? "Verifying…" : "Verify & Continue"}
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Reset password */}
              {forgotStep === "reset" && (
                <>
                  <label style={{ display: "block", marginBottom: 10 }}>
                    <span style={{ fontSize: 13, color: COLORS.text }}>New password</span>
                    <input
                      type="password"
                      placeholder="Enter new password"
                      value={resetPass}
                      onChange={(e) => setResetPass(e.target.value)}
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    <span style={{ fontSize: 13, color: COLORS.text }}>Confirm new password</span>
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      value={resetPass2}
                      onChange={(e) => setResetPass2(e.target.value)}
                      style={inputStyle}
                    />
                  </label>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                    <button type="button" style={ghostBtn} onClick={() => setForgotOpen(false)}>
                      Cancel
                    </button>
                    <button type="button" style={primaryBtn} onClick={submitPasswordReset}>
                      Update Password
                    </button>
                  </div>
                </>
              )}

              {forgotErr && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 10 }}>{forgotErr}</div>}
              {forgotMsg && <div style={{ color: COLORS.primary, fontSize: 13, marginTop: 10 }}>{forgotMsg}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Approval Pending Modal */}
      {approvalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "grid", placeItems: "center", padding: 16, zIndex: 60 }}>
          <div style={{ width: "100%", maxWidth: 480, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 18, boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: COLORS.text }}>Application under review</div>
              <button onClick={() => setApprovalOpen(false)} style={iconButton}><FiX /></button>
            </div>

            <div style={{ marginTop: 10, color: COLORS.subtext, lineHeight: 1.6 }}>
              Thanks for signing up with <b>ApnaArtist</b>. Your organiser account is awaiting approval.
              Our team will verify your details and get back to you within <b>72 hours</b>.
              <br /><br />
              <b>Want faster approval?</b> WhatsApp us at{" "}
              <a href="https://wa.me/918099975322" target="_blank" rel="noreferrer" style={{ ...linkButton, color: COLORS.primary, fontWeight: 800 }}>
                +91&nbsp;80999&nbsp;75322
              </a>{" "}
              with your <b>registered email</b>, <b>mobile number</b>, and a brief reason for urgent activation.
              <br /><br />
              Thanks for joining ApnaArtist!
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setApprovalOpen(false)} style={linkButton}>Okay</button>
            </div>
          </div>
        </div>
      )}

      {/* Not Registered Modal */}
      {notRegOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "grid", placeItems: "center", padding: 16, zIndex: 60 }}>
          <div style={{ width: "100%", maxWidth: 420, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 18, boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: COLORS.text }}>Number not registered</div>
              <button onClick={() => setNotRegOpen(false)} style={iconButton}><FiX /></button>
            </div>
            <div style={{ marginTop: 10, color: COLORS.subtext }}>
              Mobile <b>{pendingMobile}</b> is not registered with us. Please sign up to continue.
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setNotRegOpen(false)} style={linkButton}>Close</button>
              <button
                onClick={() => {
                  setNotRegOpen(false);
                  nav(`/events/signup?mobile=${encodeURIComponent(pendingMobile)}`);
                }}
                style={{ ...solidButton, width: 140, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` }}
              >
                Sign up
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Small UI helpers
   ────────────────────────────────────────────────────────── */
function Benefit({ icon, title, copy }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "36px 1fr", gap: 12 }}>
      <div style={{
        width: 36, height: 36, display: "grid", placeItems: "center",
        borderRadius: 8, background: "#fff", border: `1px solid ${COLORS.border}`
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 800, color: COLORS.text }}>{title}</div>
        <div style={{ color: COLORS.subtext, marginTop: 4 }}>{copy}</div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  marginTop: 6,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  outline: "none",
  background: "#fff",
};

const linkButton = {
  border: "none",
  background: "transparent",
  color: COLORS.subtext,
  cursor: "pointer",
  textDecoration: "none",
};

const iconButton = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34, height: 34, borderRadius: 8,
  display: "grid", placeItems: "center",
  cursor: "pointer",
};

const solidButton = {
  height: 42,
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

/* ✅ Added missing styles */
const ghostBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 42,
  padding: "0 16px",
  borderRadius: 10,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

const primaryBtn = {
  height: 42,
  padding: "0 16px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};
